import React from "react";
import { useFela } from "react-fela";

import { useTheme } from "@/hooks/useTheme";
import { Link } from "@/components/Link";
import { IBlogPostSummary } from "@/services/wordpress";

export const formatPostDate = (date: string): string =>
  new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(date));

export const BlogPostCard: React.FC<{ post: IBlogPostSummary }> = ({
  post,
}) => {
  const { css } = useFela();
  const { colors } = useTheme();

  return (
    <div className={css({ display: "flex", flexDirection: "column" })}>
      <Link
        href={`/blog/${post.slug}`}
        customStyle={{ display: "block", textDecoration: "none" }}
      >
        {post.featuredImage && (
          <img
            src={post.featuredImage.url}
            alt={post.featuredImage.alt || post.title}
            loading="lazy"
            className={css({
              width: "100%",
              aspectRatio: "16 / 9",
              objectFit: "cover",
              display: "block",
              borderRadius: 10,
            })}
          />
        )}
      </Link>

      {post.categories.length > 0 && (
        <div
          className={css({
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginTop: 20,
          })}
        >
          {post.categories.map(category => (
            <Link
              key={category.slug}
              href={`/blog/category/${category.slug}`}
              customStyle={{
                fontSize: 13,
                fontWeight: 600,
                color: colors.DARK_GREY,
                backgroundColor: colors.CULTURED,
                borderRadius: 999,
                padding: "4px 12px",
                textDecoration: "none",
              }}
            >
              {category.name}
            </Link>
          ))}
        </div>
      )}

      <Link
        href={`/blog/${post.slug}`}
        customStyle={{ textDecoration: "none" }}
      >
        <h3
          className={css({
            margin: "14px 0 0",
            fontSize: 24,
            lineHeight: 1.3,
            fontWeight: 700,
            color: colors.DARK_GREY1,
          })}
        >
          {post.title}
        </h3>
      </Link>

      <div
        className={css({
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 12,
          color: colors.GRAY,
          fontSize: 14,
        })}
      >
        {post.authorAvatar && (
          <img
            src={post.authorAvatar}
            alt={post.authorName || "Author"}
            width={24}
            height={24}
            loading="lazy"
            className={css({ borderRadius: "50%", display: "block" })}
          />
        )}
        {post.authorName && <span>{post.authorName}</span>}
        <span aria-hidden="true">·</span>
        <time dateTime={post.date}>{formatPostDate(post.date)}</time>
      </div>
    </div>
  );
};
